import { useRef } from "react";
import { LoadingOutlined, RightOutlined, TeamOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { OpenTable, Reservation } from "../../../../api/reservations";
import { SchemaModalShell } from "../../restaurant-schema/modals/SchemaModalShell";
import { useEscapeToClose } from "../../hooks/useEscapeToClose";
import { useFocusOnOpen } from "../../../../hooks/useFocusOnOpen";
import SeatBillRow from "./SeatBillRow";

/**
 * „При кой сервитьор да настаним?" — на масите на резервацията има няколко
 * отворени сметки (напр. двама сервитьори на маса 1 при лична номерация).
 * Всеки ред е сметка: сервитьор, маса, от колко време е отворена и сумата ѝ
 * (32.50 € срещу 0.00 € — при кого реално седят гостите); кликът
 * настанява към нея (`onPick` → seat с billId). Шелът е общият от
 * references/modals.md (клас Default); фонът затваря през antd, Esc — с
 * preventDefault (иначе минава и глобалното „назад").
 */
const SeatBillPickerModal = ({
  reservation,
  bills,
  nowMs,
  pendingBillId,
  onPick,
  onClose,
}: {
  // null — модалът е затворен.
  reservation: Reservation | null;
  bills: OpenTable[];
  nowMs: number;
  // Сметката, към която настаняването е в ход (спинер, останалите заключени).
  pendingBillId: number | null;
  onPick: (bill: OpenTable) => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const busy = pendingBillId !== null;
  const open = reservation !== null;
  useEscapeToClose(open, onClose);
  // Фокусът по подразбиране е на безопасния „Отказ" (Enter не настанява
  // никого) — бутонът е локален, не GhostButton (той не приема ref).
  const closeRef = useRef<HTMLButtonElement>(null);
  useFocusOnOpen(open, closeRef);

  return (
    <SchemaModalShell
      open={reservation !== null}
      icon={<TeamOutlined />}
      title={t("reservations.seatPicker.title")}
      subtitle={
        reservation
          ? t("reservations.seatPicker.subtitle", {
              guest: reservation.guestName,
              tables: reservation.tableNames.join(", "),
              count: reservation.tableNames.length,
            })
          : undefined
      }
      onClose={onClose}
      footer={
        <button
          ref={closeRef}
          type="button"
          disabled={busy}
          onClick={onClose}
          className="flex h-11 cursor-pointer items-center justify-center gap-2 rounded-[10px] border border-shade-3 bg-surface px-[18px] text-[14px] font-semibold text-ink-2 transition-colors duration-150 hover:bg-fill disabled:cursor-not-allowed disabled:opacity-50 sm:h-[42px]"
        >
          {t("reservations.seatPicker.close")}
        </button>
      }
    >
      <div className="flex flex-col gap-2">
        {bills.map((bill) => (
          <SeatBillRow
            key={bill.billId}
            bill={bill}
            nowMs={nowMs}
            disabled={busy}
            onClick={() => onPick(bill)}
            trailing={pendingBillId === bill.billId ? <LoadingOutlined /> : <RightOutlined />}
          />
        ))}
      </div>
    </SchemaModalShell>
  );
};

export default SeatBillPickerModal;
